import { isPlatformServer } from '@angular/common';
import { Inject, Optional, PLATFORM_ID, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { RoutingService } from '@app/core/services/routing.service';
import { CatalogService, FacetLinksResponse, CatalogSeoLinksResponse } from '@vestiaire/api-sdk-angular';
import { forkJoin, from, iif, Observable, of } from 'rxjs/';
import { catchError, every, map, mergeMap } from 'rxjs/operators';
import { LocaleService } from '@app/core/i18n';
import { REQUEST } from '@nguniversal/express-engine/tokens';

export interface CatalogLinksResolverResponse {
  getFacetLinks: Observable<FacetLinksResponse | null>;
  listSeoLinksCatalog: Observable<CatalogSeoLinksResponse | null>;
}

@Injectable()
export class CatalogLinksResolver implements Resolve<CatalogLinksResolverResponse> {
  constructor(
    private catalogService: CatalogService,
    private routingService: RoutingService,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<CatalogLinksResolverResponse> {
    return forkJoin({
      getFacetLinks: of(route).pipe(
        mergeMap(routeSnapshot =>
          iif(
            () => isPlatformServer(this.platformId),
            // call only on SSR
            this.catalogService.getFacetLinks(this.routingService.buildPath(routeSnapshot.url)).pipe(
              map(res => res.data),
              catchError(err => of(null))
            ),
            of(null) // skip call on browser
          )
        )
      ),
      listSeoLinksCatalog: from(route.url).pipe(
        every(url => !url.toString().match('^p-\\d+$')), // only for first page of the catalog. So we check if p-[numbers] in the url
        mergeMap(isFirstPage =>
          iif(
            () =>
              isFirstPage &&
              !(isPlatformServer(this.platformId) && this.request.headers['user-agent'] === 'noseolinks'), //  seo links should not  appear
            // if  not  first page or  user agent is defined to noseolinks in order to analyse impact
            // THEN call catalogService.listSeoLinksCatalog with current language
            this.catalogService.listSeoLinksCatalog(state.url.replace(/^\//, ''), this.localeService.getLanguage()),
            of(null) // ELSE do nothing
          )
        ),
        catchError(err => of(null))
      )
    });
  }
}
